import axios from 'axios';
import { refreshToken } from './authService'; 

const API_BASE_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', },
});

// --- Request Interceptor (Adds Token) ---
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token && !config.headers['Authorization']) { 
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log("Request Interceptor: Added Auth header");
    } else {
      // console.log("Request Interceptor: No token found or header already set");
    }
    return config;
  },
  (error) => Promise.reject(error)
);
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error); 
    } else {
      prom.resolve(token); 
    }
  });
  failedQueue = []; 
};

apiClient.interceptors.response.use(
  (response) => response, 
  async (error) => {
    const originalRequest = error.config;
    console.log(`Response Interceptor: Request to ${originalRequest.url} failed with status ${error.response?.status}`);

    // Check specifically for 401 Unauthorized errors AND ensure it's not a retry already
    // Also check that it wasn't the refresh token request itself that failed with 401
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/token/refresh/') { 
      console.log("Response Interceptor: Detected 401, attempting token refresh.");

      if (isRefreshing) {
        // If refresh is already in progress, queue this failed request
        console.log("Response Interceptor: Refresh already in progress, queueing request:", originalRequest.url);
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          // Once refresh is done, retry the original request with the new token
          console.log("Response Interceptor: Retrying queued request with new token:", originalRequest.url);
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest); // Retry with original config but new token
        }).catch(err => {
          return Promise.reject(err); // Propagate refresh error
        });
      }

      // --- Start Refresh Process ---
      originalRequest._retry = true; 
      isRefreshing = true;

      const currentRefreshToken = localStorage.getItem('refreshToken');
      if (!currentRefreshToken) {
          console.log("Response Interceptor: No refresh token found. Cannot refresh.");
          isRefreshing = false;
          // Trigger logout (needs access to logout function or uses event/redirect)
          // Ideally, signal AuthContext to logout
          window.dispatchEvent(new CustomEvent('auth-logout-event')); // Dispatch custom event
          return Promise.reject(error); // Reject original error
      }

      try {
        console.log("Response Interceptor: Calling refreshToken service...");
        const refreshResponse = await refreshToken(currentRefreshToken); // Call the service
        const newAccessToken = refreshResponse.access;

        console.log("Response Interceptor: Refresh successful. New token obtained.");
        localStorage.setItem('accessToken', newAccessToken); // Store the new token

        // Update the Authorization header for the original failed request
        apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken; 
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken; 

        processQueue(null, newAccessToken); 

        console.log("Response Interceptor: Retrying original request:", originalRequest.url);
        return apiClient(originalRequest); 

      } catch (refreshError) {
        console.error("Response Interceptor: Refresh token failed.", refreshError);
        processQueue(refreshError, null); 
        isRefreshing = false;
         // Trigger logout (needs access to logout function or uses event/redirect)
         window.dispatchEvent(new CustomEvent('auth-logout-event')); 
        return Promise.reject(refreshError); 

      } finally {
        
      }

    } // End of 401 handling block
    // For errors other than 401, just reject them
    console.log("Response Interceptor: Error is not 401 or already retried. Rejecting.");
    return Promise.reject(error);
  }
);

export default apiClient;
